import { proposalDisplayValue } from "../../shared/model/proposalDisplayValue";
import { PROPOSAL_DECISIONS } from "@/features/catalog_case_review/shared/constants/caseReviewDecisions";
import { hasProposalVersions, proposalVersionText } from "@/features/catalog_case_review/shared/constants/caseReviewLabels";
import type { ProposalDecision, ProposalVersion } from "@/features/catalog_case_review/shared/model/agentProposal";
import { DOCK_LABELS, PROPOSAL_STATE_LABELS } from "../../shared/constants/pageAgentLabels";
import { PROPOSAL_CARD_CLOSED } from "../../shared/constants/pageAgentIds";
import type { ProposalCardChange, ProposalCardRow } from "../../shared/model/agentTurn";

interface ProposalCardProps {
  rows: ProposalCardRow[];
  decisions: Record<string, ProposalDecision>;
  versions: Record<string, ProposalVersion>;
  /** The rows the form has let go of: they offer no Accept, Reject, Undo or Redo. */
  closed: ReadonlySet<string>;
  onAccept: (key: string) => void;
  onReject: (key: string) => void;
  onUndo: (key: string) => void;
  onRedo: (key: string) => void;
  onGoToField: (row: ProposalCardRow) => void;
}

/** The fields a proposal names, joined the way the row is titled: "Preferred Term, Lower Level Term". */
function fieldsLabel(changes: ProposalCardChange[]): string {
  return changes.map((change) => change.fieldLabel).join(", ");
}

function shown(value: string): string {
  return value === "" ? DOCK_LABELS.proposalEmpty : value;
}

/**
 * Every change the agent proposed during the question, one row per proposal with every field's
 * old → new and its reason, and the same ✓/✕/Undo/Redo the form's row offers. Drawn from the tool
 * results, so it lists every change whatever the model went on to say.
 */
export function ProposalCard({ rows, decisions, versions, closed, onAccept, onReject, onUndo, onRedo, onGoToField }: ProposalCardProps) {
  const pending = rows.filter((row) => !closed.has(row.key)
    && (decisions[row.key] ?? PROPOSAL_DECISIONS.pending) === PROPOSAL_DECISIONS.pending).length;
  return (
    <section className="pa-card" aria-label={DOCK_LABELS.proposalsTitle}>
      <h3 className="pa-card-head">{DOCK_LABELS.proposalsTitle} ({rows.length})</h3>
      <ul className="pa-card-rows">
        {rows.map((row) => {
          const decision = decisions[row.key] ?? PROPOSAL_DECISIONS.pending;
          const label = fieldsLabel(row.changes);
          const version = versions[row.key];
          const redo = !closed.has(row.key) && version !== undefined && version.canRedo && (
            <button type="button" className="pa-button pa-button-secondary pa-button-small" onClick={() => onRedo(row.key)}
              aria-label={`${DOCK_LABELS.proposalRedo} ${label}`}>{DOCK_LABELS.proposalRedo}</button>
          );
          return (
            <li className="pa-card-row" key={row.key} data-decision={closed.has(row.key) ? PROPOSAL_CARD_CLOSED : decision}>
              <span className="pa-card-field">{label}
                <button type="button" className="pa-link" onClick={() => onGoToField(row)}
                  aria-label={`${DOCK_LABELS.proposalGoToField} ${label}`}>{DOCK_LABELS.proposalGoToField}</button>
              </span>
              <ul className="pa-card-changes">
                {row.changes.map((change) => (
                  <li className="pa-card-change" key={change.fieldId} aria-label={`${change.fieldLabel}: ${shown(proposalDisplayValue(change.fieldId, change.previousValue))} to ${proposalDisplayValue(change.fieldId, change.value)}`}>
                    {row.changes.length > 1 && <span className="pa-card-change-field">{change.fieldLabel}</span>}
                    <span className="pa-card-old" data-empty={change.previousValue === ""}>{shown(proposalDisplayValue(change.fieldId, change.previousValue))}</span>
                    <span aria-hidden="true">→</span>
                    <span className="pa-card-new">{proposalDisplayValue(change.fieldId, change.value)}</span>
                  </li>
                ))}
              </ul>
              {!closed.has(row.key) && decision === PROPOSAL_DECISIONS.pending && version !== undefined && hasProposalVersions(version) && (
                <span className="pa-card-version">{proposalVersionText(version)}</span>
              )}
              <span className="pa-card-why">{row.rationale}</span>
              <span className="pa-card-actions">
                {closed.has(row.key) ? (
                  <span className="pa-card-state" data-decision={PROPOSAL_CARD_CLOSED}>{DOCK_LABELS.proposalClosed}</span>
                ) : decision === PROPOSAL_DECISIONS.pending ? (
                  <>
                    <button type="button" className="pa-button pa-button-small" onClick={() => onAccept(row.key)}
                      aria-label={`${DOCK_LABELS.proposalAccept} ${label}`}>{DOCK_LABELS.proposalAccept}</button>
                    <button type="button" className="pa-button pa-button-secondary pa-button-small" onClick={() => onReject(row.key)}
                      aria-label={`${DOCK_LABELS.proposalReject} ${label}`}>{DOCK_LABELS.proposalReject}</button>
                    <button type="button" className="pa-button pa-button-secondary pa-button-small" onClick={() => onUndo(row.key)}
                      aria-label={`${DOCK_LABELS.proposalUndo} ${label}`}>{DOCK_LABELS.proposalUndo}</button>
                    {redo}
                  </>
                ) : (
                  <>
                    <span className="pa-card-state" data-decision={decision}>{PROPOSAL_STATE_LABELS[decision]}</span>
                    {redo}
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="pa-card-foot">{pending > 0 ? DOCK_LABELS.proposalsPending : DOCK_LABELS.proposalsDecided}</p>
    </section>
  );
}
